/**
 * Pure text and number helpers for the screens. Names and descriptions of game ids always come
 * from `GameApi.describe`; what lives here is how the menus format them, and the fallbacks that
 * keep a screen readable when an id has no entry yet.
 */

import { RANKS } from '../../meta/types';
import type { RangerRecord } from '../../meta/types';

export type Describe = (id: string) => { name: string; text: string };

/** `sam-site` -> `Sam Site`: the fallback name for an id nobody has described. */
export function humanise(id: string): string {
  const words = id.split(/[-_.:\s]+/).filter((w) => w !== '');
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/**
 * `describe` that never throws and never returns an empty name. The real API answers an unknown
 * id with the id itself (`day`, `sandstorm`): that, too, is humanised rather than shown raw.
 */
export function safeDescribe(describe: Describe, id: string): { name: string; text: string } {
  try {
    const d = describe(id);
    const name = typeof d.name === 'string' && d.name.trim() !== '' && d.name !== id ? d.name : humanise(id);
    return { name, text: typeof d.text === 'string' ? d.text : '' };
  } catch {
    return { name: humanise(id), text: '' };
  }
}

/** True when `describe` knows the id (it answered with a name that is not just the id). */
export function isDescribed(describe: Describe, id: string): boolean {
  try {
    const name = describe(id).name;
    return typeof name === 'string' && name.trim() !== '' && name !== id;
  } catch {
    return false;
  }
}

/** The slider's four detents (02 §12), by `Operation.difficulty`. */
export const DIFFICULTY_NAMES = ['Milk Run', 'Standard', 'Hard', 'Suicide'] as const;

export function difficultyName(difficulty: number): string {
  return DIFFICULTY_NAMES[Math.min(3, Math.max(0, Math.round(difficulty)))] ?? 'Standard';
}

/**
 * Cumulative merit needed for each rank (02 §11). Mirrored here because the contract exposes
 * the rank names but not the ladder; only the dossier's progress bar reads it.
 */
export const RANK_MERIT = [0, 1500, 4000, 8000, 13000, 20000, 28000, 38000, 50000, 65000, 85000, 110000] as const;

export function rankIndex(rank: number): number {
  return Math.min(RANKS.length - 1, Math.max(0, Math.round(rank)));
}

export function rankAbbr(rank: number): string {
  return RANKS[rankIndex(rank)] ?? 'PFC';
}

export interface MeritProgress {
  /** Merit earned since the current rank's threshold, and what the next rank needs beyond it. */
  into: number;
  span: number;
  /** 0-1; 1 at the top rank. */
  fraction: number;
  /** Abbreviation of the next rank, or null at the top. */
  next: string | null;
}

export function meritProgress(rank: number, merit: number): MeritProgress {
  const i = rankIndex(rank);
  const base = RANK_MERIT[i] ?? 0;
  const target = RANK_MERIT[i + 1];
  if (target === undefined) return { into: Math.max(0, merit - base), span: 0, fraction: 1, next: null };
  const span = target - base;
  const into = Math.min(span, Math.max(0, merit - base));
  return { into, span, fraction: span > 0 ? into / span : 1, next: RANKS[i + 1] ?? null };
}

export function fullName(r: RangerRecord): string {
  return `${r.firstName} ${r.surname}`.trim();
}

/** `SGT "VIPER" MARLOWE`. */
export function serviceName(r: RangerRecord): string {
  const call = r.callsign.trim() === '' ? '' : ` "${r.callsign}"`;
  return `${rankAbbr(r.rank)}${call} ${r.surname}`.trim();
}

export type ChipTone = 'green' | 'amber' | 'red' | 'dim';

export interface Availability {
  /** The chip on the company rail and the dossier (08 §2.2). */
  chip: string;
  tone: ChipTone;
  available: boolean;
  /** Why he cannot deploy; empty when he can. */
  reason: string;
}

function missions(n: number): string {
  return n === 1 ? '1 MISSION' : `${n} MISSIONS`;
}

/**
 * Status chip and deployability (04 §4.4). Only `ready` deploys - `GameApi.deploy` refuses every
 * other status - so a lightly wounded ranger is one the API still reports as `ready`.
 */
export function availability(r: RangerRecord): Availability {
  const out = Math.max(0, r.recoveryMissions);
  switch (r.status) {
    case 'ready':
      return r.fatigue >= 2
        ? { chip: `FATIGUED ${r.fatigue}`, tone: 'amber', available: true, reason: '' }
        : { chip: 'READY', tone: 'green', available: true, reason: '' };
    case 'wounded':
      return {
        chip: out > 0 ? `WOUNDED ${out}` : 'WOUNDED',
        tone: 'red',
        available: false,
        reason: out > 0 ? `SERIOUSLY WOUNDED: OUT FOR ${missions(out)}` : 'SERIOUSLY WOUNDED: NOT YET FIT',
      };
    case 'critical':
      return { chip: `CRITICAL ${out}`, tone: 'red', available: false, reason: `CRITICAL: OUT FOR ${missions(out)}` };
    case 'pow':
      return { chip: 'POW', tone: 'red', available: false, reason: 'PRISONER OF WAR: RESCUE HIM OR LOSE HIM' };
    case 'mia':
      return { chip: `MIA ${out}`, tone: 'amber', available: false, reason: `WALKING OUT: BACK IN ${missions(out)}` };
    case 'kia':
      return { chip: 'KIA', tone: 'red', available: false, reason: 'KILLED IN ACTION' };
    case 'lost':
      return { chip: 'LOST', tone: 'red', available: false, reason: 'POW - NEVER RECOVERED' };
    case 'retired':
      return { chip: 'RETIRED', tone: 'dim', available: false, reason: 'RETIRED FROM SERVICE' };
  }
}

/** Stamina cost of fatigue (04 §4.4): -15 % per point above 1. */
export function fatiguePenaltyPercent(fatigue: number): number {
  return fatigue >= 2 ? (fatigue - 1) * 15 : 0;
}

/** `x1.25`. */
export function multiplier(value: number): string {
  return `x${value.toFixed(2).replace(/0$/, '')}`;
}

/**
 * Requisition Points: the operation's currency (04 §6). The abbreviation is on five screens and
 * the words behind it were on none of them - a player could finish a whole operation without ever
 * being told what the two letters stood for. They live here now, and every bare `RP` carries them.
 */
export const RP_MEANING = 'REQUISITION POINTS - THE CURRENCY OF THIS OPERATION. IT BUYS WHAT GOES IN YOUR'
  + ' PODS, INTEL AT THE BRIEFING, REPLACEMENTS AND PRIORITY MEDEVAC. IT DOES NOT CARRY OVER.';

/** `REQUISITION 30 RP`: spelled out in full where a player first meets the term, on the title. */
export const requisitionLine = (rp: number): string => `REQUISITION ${rp} RP`;
